import { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { DOOR_FRONT, URS, VIEW_BOX, Wordmark } from "./wordmark";

// The wordmark building itself, for splash screens: a ball drops on the left
// foot of the doorway, rolls the arch out over the top and along the
// threshold, folds flat into the last corner, the doorway takes its depth,
// and "urs" slides out of it. Ends on the static Wordmark.
//
// The intro runs on one clock per page load, so a splash that replaces
// another (auth loading, then boards loading) carries on where the last one
// was instead of starting over, and later splashes show the static mark.

const LETTERS = URS.match(/M[^Z]*Z/g) ?? [];
// The doorway's band: 10 wide, its centre line up the left leg, over the arch,
// down the right leg and back along the threshold.
const CENTER = "M9.4 64V29.25A18.25 18.25 0 0 1 45.9 29.25V59H4.4";
const W = 5;
const DEPTH: [number, number] = [5, -3.5];
const COPIES = 16;
const FALL = 55;
const LANDED = 0.68;
const TRAVEL = 1.25;
const LETTERS_AT = 2.55;
const DURATION = 3.1;

let clockStart: number | null = null;

const clamp = (x: number) => Math.min(1, Math.max(0, x));
const seg = (t: number, a: number, b: number) => clamp((t - a) / (b - a));
const inOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const inOutSpeed = (t: number) => (t < 0.5 ? 12 * t * t : 3 * Math.pow(2 - 2 * t, 2)) / 3;
const out = (t: number) => 1 - Math.pow(1 - t, 3);
const back = (t: number) => 1 + 2.9 * Math.pow(t - 1, 3) + 1.9 * Math.pow(t - 1, 2);
const bump = (t: number, at: number, width: number) => Math.exp(-Math.pow((t - at) / width, 2));

// The ball's outline along the band: x runs along the path, y across it, with
// the back and front corners rounded separately so each can fold flat.
function slab(rear: number, front: number) {
  const rb = Math.min(rear, W);
  const rf = Math.min(front, W);
  return (
    `M${-W + rb} ${-W}L${W - rf} ${-W}A${rf} ${rf} 0 0 1 ${W} ${-W + rf}L${W} ${W - rf}` +
    `A${rf} ${rf} 0 0 1 ${W - rf} ${W}L${-W + rb} ${W}A${rb} ${rb} 0 0 1 ${-W} ${W - rb}` +
    `L${-W} ${-W + rb}A${rb} ${rb} 0 0 1 ${-W + rb} ${-W}Z`
  );
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function introOver() {
  return clockStart !== null && (performance.now() - clockStart) / 1000 >= DURATION;
}

/**
 * `onDone` is called once the mark stands complete: at the end, or at once
 * without motion. `replay` starts the clock over if the intro already played,
 * for a screen that shows it once more in full.
 */
export function WordmarkIntro({
  className,
  onDone,
  replay = false,
}: {
  className?: string;
  onDone?: () => void;
  replay?: boolean;
}) {
  const [done, setDone] = useState(() => {
    if (replay && introOver()) clockStart = null;
    return introOver() || prefersReducedMotion();
  });
  useEffect(() => {
    if (done) onDone?.();
  }, [done, onDone]);
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const band = useRef<SVGPathElement>(null);
  const head = useRef<SVGPathElement>(null);
  const ball = useRef<SVGCircleElement>(null);
  const depth = useRef<(SVGUseElement | null)[]>([]);
  const letters = useRef<(SVGPathElement | null)[]>([]);

  useEffect(() => {
    if (done || !band.current || !head.current || !ball.current) return;
    const bandEl = band.current;
    const headEl = head.current;
    const ballEl = ball.current;
    const length = bandEl.getTotalLength();
    const at = (l: number) => bandEl.getPointAtLength(Math.max(0, Math.min(length, l)));
    const foot = at(W);
    clockStart ??= performance.now();
    const start = clockStart;

    const draw = (t: number) => {
      let reveal = 0;
      let extrude = 0;
      if (t < LANDED) {
        const fall = seg(t, 0, 0.42);
        const hop = seg(t, 0.42, 0.66);
        const dy = t < 0.42 ? -FALL * (1 - fall * fall) : -FALL * 0.14 * 4 * hop * (1 - hop);
        const squash = 0.28 * bump(t, 0.42, 0.035) + 0.14 * bump(t, 0.66, 0.03);
        ballEl.setAttribute(
          "transform",
          `translate(${foot.x} ${foot.y + dy + W * squash}) scale(${1 + squash} ${1 - squash})`,
        );
        ballEl.style.opacity = String(seg(t, 0, 0.08));
        headEl.style.display = "none";
      } else {
        ballEl.style.display = "none";
        headEl.style.display = "";
        const t1 = LANDED + 0.12;
        const end = t1 + TRAVEL;
        const u = seg(t, t1, end);
        const l = W + (length - 2 * W) * inOut(u);
        const stretch = u > 0 && u < 1 ? 1 + 0.22 * inOutSpeed(u) : 1;
        const rear = W * (1 - out(seg(t, LANDED, LANDED + 0.16)));
        const front = W * (1 - out(seg(t, end - 0.03, end + 0.18)));
        const a = at(l - 0.5);
        const b = at(l + 0.5);
        const p = at(l);
        const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
        headEl.setAttribute("d", slab(rear, front));
        headEl.setAttribute(
          "transform",
          `translate(${p.x} ${p.y}) rotate(${angle}) scale(${stretch} ${1 / stretch})`,
        );
        reveal = l - rear;
        extrude = back(seg(t, end + 0.1, end + 0.7));
      }
      bandEl.setAttribute(
        "stroke-dasharray",
        reveal < 0.01 ? `0 ${length * 3}` : `${reveal} ${length * 3}`,
      );
      depth.current.forEach((use, i) => {
        const k = ((COPIES - i) / COPIES) * extrude;
        use?.setAttribute("transform", `translate(${DEPTH[0] * k} ${DEPTH[1] * k})`);
      });
      letters.current.forEach((letter, i) => {
        const p = out(seg(t, LETTERS_AT + i * 0.09, LETTERS_AT + i * 0.09 + 0.45));
        if (!letter) return;
        letter.style.opacity = String(p);
        letter.setAttribute("transform", `translate(${-14 * (1 - p)} 0)`);
      });
    };

    let frame = 0;
    const tick = (now: number) => {
      const t = (now - start) / 1000;
      if (t >= DURATION) {
        setDone(true);
        return;
      }
      draw(t);
      frame = requestAnimationFrame(tick);
    };
    draw((performance.now() - start) / 1000);
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [done]);

  const sizeCls = "h-11";
  if (done) return <Wordmark as="div" className={cn(sizeCls, className)} />;

  return (
    <svg
      viewBox={VIEW_BOX}
      overflow="visible"
      role="img"
      aria-label="Ours"
      className={cn("block w-auto shrink-0 text-ink", sizeCls, className)}
    >
      <defs>
        <mask
          id={`${id}-band`}
          maskUnits="userSpaceOnUse"
          x={-100}
          y={-200}
          width={400}
          height={400}
        >
          <path
            ref={band}
            d={CENTER}
            fill="none"
            stroke="#fff"
            strokeWidth={W * 2 + 0.2}
            strokeLinejoin="miter"
            strokeDasharray="0 1000"
          />
        </mask>
        <clipPath id={`${id}-door`}>
          <path d={DOOR_FRONT} clipRule="evenodd" />
        </clipPath>
        {/* The doorway's face so far; drawn once in the accent and stacked
            behind itself in the darker accent for the depth. */}
        <g id={`${id}-face`}>
          <path d={DOOR_FRONT} fillRule="evenodd" mask={`url(#${id}-band)`} />
          {/* The clip sits on a wrapper: on the path it would move with the ball. */}
          <g clipPath={`url(#${id}-door)`}>
            <path ref={head} style={{ display: "none" }} />
          </g>
          <circle ref={ball} r={W} style={{ opacity: 0 }} />
        </g>
      </defs>
      {Array.from({ length: COPIES }, (_, i) => (
        <use
          key={i}
          ref={(el) => {
            depth.current[i] = el;
          }}
          href={`#${id}-face`}
          className="fill-accent-2"
        />
      ))}
      <use href={`#${id}-face`} className="fill-accent" />
      {LETTERS.map((d, i) => (
        <path
          key={i}
          ref={(el) => {
            letters.current[i] = el;
          }}
          d={d}
          fill="currentColor"
          style={{ opacity: 0 }}
        />
      ))}
    </svg>
  );
}
